import { useMemo } from 'react';
import { useOverviewState } from './useOverviewState';
import type { VacationOverviewDocument } from '../models/vacationOverview.types';

export const DASHBOARD_FILTER_KEYS = {
	collaborators: 'pessoa',
	clients: 'cliente',
} as const;

export interface DashboardFilters {
	collaborators: string[];
	clients: string[];
}

export interface FilterOptions {
	collaborators: Array<{ value: string; label: string }>;
	clients: Array<{ value: string; label: string }>;
}

function sortedOptions(entries: Array<[string, string]>) {
	return entries
		.map(([value, label]) => ({ value, label }))
		.sort((a, b) => a.label.localeCompare(b.label));
}

export function useVacationOverviewFilters(documents: VacationOverviewDocument[]) {
	const { getAll, setMany, clear } = useOverviewState();

	const collaboratorsStr = getAll(DASHBOARD_FILTER_KEYS.collaborators).join('|');
	const clientsStr = getAll(DASHBOARD_FILTER_KEYS.clients).join('|');

	const filters = useMemo<DashboardFilters>(
		() => ({
			collaborators: getAll(DASHBOARD_FILTER_KEYS.collaborators),
			clients: getAll(DASHBOARD_FILTER_KEYS.clients),
		}),
		[collaboratorsStr, clientsStr]
	);

	const filterOptions = useMemo((): FilterOptions => {
		const collaborators = new Map<string, string>();
		const clients = new Map<string, string>();

		documents
			.filter(d => d.active && d.inBase)
			.forEach(doc => {
				collaborators.set(doc.collaboratorId, doc.name);
				doc.clientNames.forEach(client => clients.set(client, client));
			});

		return {
			collaborators: sortedOptions(Array.from(collaborators.entries())),
			clients: sortedOptions(Array.from(clients.entries())),
		};
	}, [documents]);

	const filteredDocuments = useMemo(() => {
		const { collaborators, clients } = filters;
		return documents.filter(doc => {
			if (collaborators.length > 0 && !collaborators.includes(doc.collaboratorId)) return false;
			if (clients.length > 0 && !clients.some(c => doc.clientNames.includes(c))) return false;
			return true;
		});
	}, [documents, filters]);

	const setFilter = (key: keyof DashboardFilters, values: string[]) =>
		setMany({ [DASHBOARD_FILTER_KEYS[key]]: values });

	const clearFilters = () => clear(Object.values(DASHBOARD_FILTER_KEYS));

	return { filters, setFilter, clearFilters, filterOptions, filteredDocuments };
}
